import { useCallback, useEffect, useState } from "react";
import {
  createDealProductType,
  createDealSaleType,
  createDealTypeSlot,
  deleteDealProductType,
  deleteDealSaleType,
  deleteDealTypeSlot,
  listDealProductSaleLinks,
  listDealProductTypes,
  listDealSaleTypes,
  listDealTypeSlots,
  patchDealProductType,
  patchDealSaleType,
  setDealProductSaleLinks,
  type OpsDealProductSaleLink,
  type OpsDealProductType,
  type OpsDealSaleType,
  type OpsDealTypeSlot,
} from "@/lib/opsDealTypesClient";
import type { OpsDealTypeSlotKind } from "@/lib/opsDealTypes/opsDealTypeKeys";

export type DealTypesSlider = "product" | "sale";

export function useDealTypes(organizationId: string | null) {
  const [slider, setSlider] = useState<DealTypesSlider>("product");
  const [productTypes, setProductTypes] = useState<OpsDealProductType[]>([]);
  const [saleTypes, setSaleTypes] = useState<OpsDealSaleType[]>([]);
  const [links, setLinks] = useState<OpsDealProductSaleLink[]>([]);
  const [slots, setSlots] = useState<OpsDealTypeSlot[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const reloadCatalog = useCallback(async () => {
    if (!organizationId) {
      setProductTypes([]);
      setSaleTypes([]);
      setLinks([]);
      return;
    }
    setLoading(true);
    setMsg(null);
    const [p, s, l] = await Promise.all([
      listDealProductTypes(),
      listDealSaleTypes(),
      listDealProductSaleLinks(),
    ]);
    setLoading(false);
    if (!p.ok) {
      setMsg(p.error);
      return;
    }
    if (!s.ok) {
      setMsg(s.error);
      return;
    }
    if (!l.ok) {
      setMsg(l.error);
      return;
    }
    setProductTypes(p.data.product_types ?? []);
    setSaleTypes(s.data.sale_types ?? []);
    setLinks(l.data.links ?? []);
  }, [organizationId]);

  useEffect(() => {
    void reloadCatalog();
  }, [reloadCatalog]);

  useEffect(() => {
    const productId = slider === "product" ? selectedProductId : null;
    const saleId = slider === "sale" ? selectedSaleId : null;
    if (!productId && !saleId) {
      setSlots([]);
      return;
    }
    setSlots([]);
    void (async () => {
      const r = await listDealTypeSlots(
        productId ? { productTypeId: productId } : { saleTypeId: saleId ?? undefined },
      );
      if (!r.ok) {
        setMsg(r.error);
        setSlots([]);
        return;
      }
      setSlots(r.data.slots ?? []);
    })();
  }, [slider, selectedProductId, selectedSaleId]);

  useEffect(() => {
    if (slider === "product" && selectedProductId && !productTypes.some((p) => p.id === selectedProductId)) {
      setSelectedProductId(productTypes[0]?.id ?? null);
    }
  }, [productTypes, selectedProductId, slider]);

  useEffect(() => {
    if (slider === "sale" && selectedSaleId && !saleTypes.some((s) => s.id === selectedSaleId)) {
      setSelectedSaleId(saleTypes[0]?.id ?? null);
    }
  }, [saleTypes, selectedSaleId, slider]);

  const selectedProduct = productTypes.find((p) => p.id === selectedProductId) ?? null;
  const selectedSale = saleTypes.find((s) => s.id === selectedSaleId) ?? null;
  const linkedSaleIdsForProduct = links
    .filter((l) => l.product_type_id === selectedProductId)
    .map((l) => l.sale_type_id);

  async function createProduct(displayName: string) {
    const r = await createDealProductType({ display_name: displayName });
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    await reloadCatalog();
    setSelectedProductId(r.data.product_type.id);
    setSlider("product");
  }

  async function createSale(displayName: string) {
    const r = await createDealSaleType({ display_name: displayName });
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    await reloadCatalog();
    setSelectedSaleId(r.data.sale_type.id);
    setSlider("sale");
  }

  async function saveProduct(patch: {
    display_name: string;
    description: string;
    is_active: boolean;
    metadata?: Record<string, unknown>;
  }) {
    if (!selectedProductId) return;
    const r = await patchDealProductType(selectedProductId, patch);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    await reloadCatalog();
  }

  async function saveSale(patch: {
    display_name: string;
    description: string;
    is_active: boolean;
    metadata?: Record<string, unknown>;
  }) {
    if (!selectedSaleId) return;
    const r = await patchDealSaleType(selectedSaleId, patch);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    await reloadCatalog();
  }

  async function removeProduct() {
    if (!selectedProductId) return;
    const r = await deleteDealProductType(selectedProductId);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setSelectedProductId(null);
    await reloadCatalog();
  }

  async function removeSale() {
    if (!selectedSaleId) return;
    const r = await deleteDealSaleType(selectedSaleId);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setSelectedSaleId(null);
    await reloadCatalog();
  }

  async function saveLinks(saleTypeIds: string[]) {
    if (!selectedProductId) return;
    const r = await setDealProductSaleLinks(selectedProductId, saleTypeIds);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setLinks((prev) => [
      ...prev.filter((l) => l.product_type_id !== selectedProductId),
      ...(r.data.links ?? []),
    ]);
  }

  async function addSlot(input: {
    label: string;
    slot_kind: OpsDealTypeSlotKind;
    packet_layer?: "ops" | "plant";
    required?: boolean;
    slot_key?: string;
  }) {
    const body =
      slider === "product" && selectedProductId
        ? {
            product_type_id: selectedProductId,
            label: input.label,
            slot_kind: input.slot_kind,
            packet_layer: input.packet_layer,
            required: input.required,
            slot_key: input.slot_key,
          }
        : slider === "sale" && selectedSaleId
          ? {
              sale_type_id: selectedSaleId,
              label: input.label,
              slot_kind: input.slot_kind,
              packet_layer: "ops" as const,
              required: input.required,
              slot_key: input.slot_key,
            }
          : null;
    if (!body) return;
    const r = await createDealTypeSlot(body);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setSlots((prev) => [...prev, r.data.slot]);
  }

  async function removeSlot(id: string) {
    const r = await deleteDealTypeSlot(id);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setSlots((prev) => prev.filter((s) => s.id !== id));
  }

  return {
    slider,
    setSlider,
    loading,
    msg,
    setMsg,
    productTypes,
    saleTypes,
    slots,
    selectedProduct,
    selectedSale,
    selectedProductId,
    selectedSaleId,
    setSelectedProductId,
    setSelectedSaleId,
    linkedSaleIdsForProduct,
    reloadCatalog,
    createProduct,
    createSale,
    saveProduct,
    saveSale,
    removeProduct,
    removeSale,
    saveLinks,
    addSlot,
    removeSlot,
  };
}
